import { Contact, ContactField } from "../../services/contacts";

/** When a contact arrived: "25 de out. de 2026, 14:30" / "Oct 25, 2026, 2:30 PM" ("" without a date). */
export const contactDate = (iso: string | null, lang: string) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat(lang, { dateStyle: "medium", timeStyle: "short" }).format(date)
    : "";
};

const isEmailField = (field: ContactField) => field.type === "EMAIL" || field.key === "email";
const isPhoneField = (field: ContactField) => field.type === "PHONE" || field.key === "phone";

/**
 * An e-mail safe to answer with a mailto: link. The visitor types it, and characters valid in an address
 * (? & = %) would add copies or a text of theirs to the owner's reply (mailto:a?bcc=…&body=…): no link for those.
 */
export const isSafeEmail = (value: string) => /^[^\s@?&=%/#]+@[^\s@?&=%/#]+\.[^\s@?&=%/#]+$/.test(value);

/** The contact's e-mail (the first e-mail field that is one), to answer. */
export const emailOf = (contact: Contact) =>
  contact.fields.find(field => isEmailField(field) && isSafeEmail(field.value))?.value;

/** The contact's phone (the first phone field), to answer on WhatsApp. */
export const phoneOf = (contact: Contact) => contact.fields.find(isPhoneField)?.value;

/**
 * The digits WhatsApp can reach, with the country code, or null. A number written with its country (+1 415…,
 * 0044…) is kept as it is (10 to 15 digits); one without it is Brazilian: 10 or 11 digits get 55, and 12 or 13 must
 * already start with it (so "048 99945-6599", with the 0 before the area code, is refused). The same rule as the
 * API's FormWhatsAppNotice::phone().
 */
export const whatsappDigits = (phone: string) => {
  const raw = phone.trim();
  const withCountry = raw.startsWith("+") || raw.startsWith("00");
  let digits = raw.replace(/\D+/g, "");
  if (raw.startsWith("00")) digits = digits.slice(2);
  if (!withCountry) {
    if (digits.length === 10 || digits.length === 11) digits = `55${digits}`;
    else if (!(digits.startsWith("55") && (digits.length === 12 || digits.length === 13))) return null;
  }
  return digits.length >= 10 && digits.length <= 15 ? digits : null;
};

/** A phone to talk to on WhatsApp (see whatsappDigits). */
export const whatsappLink = (phone: string) => {
  const digits = whatsappDigits(phone);
  return digits ? `https://wa.me/${digits}` : null;
};

export { isEmailField, isPhoneField };
